import { useTranslation } from "react-i18next"

import { CircleX, FolderInput } from "@/components/icons"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

import { formatBytes, getReadableFileType, renderFileIcon } from "./file-picker/utils"

type ChatFooterFileItemProps = {
  file: File
  isUploading: boolean
  isMoving: boolean
  projectId?: string
  onRemove: () => void
  onMoveToProject: () => void
}

const iconButtonStyles =
  "flex size-6 cursor-pointer items-center justify-center rounded-full bg-card text-card-foreground ring-1 ring-border shadow-sm transition-[color,background-color,box-shadow,scale] duration-150 hover:scale-110 hover:bg-accent hover:text-accent-foreground hover:shadow-md active:scale-95"

export function ChatFooterFileItem({
  file,
  isUploading,
  isMoving,
  projectId,
  onRemove,
  onMoveToProject,
}: ChatFooterFileItemProps) {
  const { t } = useTranslation()
  const isImage = file.type.startsWith("image")
  const showButtons = !isUploading && !isMoving

  if (isImage) {
    return (
      <section className="group bg-card hover:bg-accent/50 relative shrink-0 rounded-lg border p-1.5 transition-colors">
        <img
          alt="file"
          className="size-[42px] rounded object-cover"
          src={URL.createObjectURL(file)}
        />
        {isUploading && (
          <span className="bg-warning text-warning-foreground border-foreground absolute -top-2 left-2 rounded border px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
            {t("questioninput.uploading")}
          </span>
        )}
        {showButtons && (
          <div className="absolute -top-2.5 -right-1.5 flex items-center gap-1 opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100">
            {projectId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className={iconButtonStyles}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault()
                      onMoveToProject()
                    }}
                  >
                    <FolderInput className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("questioninput.move_to_project")}</p>
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`${iconButtonStyles} hover:bg-destructive/10 hover:text-destructive`}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault()
                    onRemove()
                  }}
                >
                  <CircleX className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("questioninput.remove_image")}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </section>
    )
  }

  return (
    <section className="group bg-card hover:bg-accent/50 relative shrink-0 rounded-lg border px-3 py-2 transition-colors">
      <div className="flex items-center gap-2.5">
        <div className="flex-shrink-0">{renderFileIcon(file.name, file.type, false)}</div>
        <div className="max-w-[140px] min-w-0">
          <div className="text-foreground truncate text-xs font-medium">{file.name}</div>
          <div className="text-muted-foreground mt-0.5 flex items-center gap-1 text-[10px]">
            <span>{getReadableFileType(file.name, file.type)}</span>
            <span>•</span>
            <span>{formatBytes(file.size)}</span>
          </div>
        </div>
      </div>
      {isUploading && (
        <span className="bg-warning text-warning-foreground border-foreground absolute -top-2 left-2 rounded border px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
          {t("questioninput.uploading")}
        </span>
      )}
      {showButtons && (
        <div className="absolute -top-2.5 -right-1.5 flex items-center gap-1 opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100">
          {projectId && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={iconButtonStyles}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault()
                    onMoveToProject()
                  }}
                >
                  <FolderInput className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("questioninput.move_to_project")}</p>
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={`${iconButtonStyles} hover:bg-destructive/10 hover:text-destructive`}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault()
                  onRemove()
                }}
              >
                <CircleX className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("questioninput.remove_file")}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </section>
  )
}
